import type { Goal } from '@/api/generated/model';
import { retirementCalendarYear } from '@/features/planning/birthYear';
import type { NetWorthTimelineResult } from '@/features/planning/netWorthTimeline';
import { formatTooltipLabel } from '@/features/planning/netWorthTimeline';
import { useLanguage } from '@/i18n/language-hooks';
import { formatCurrency, useDefaultCurrency } from '@/lib/utils';
import { format } from 'date-fns';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface ProjectedNetWorthChartProps {
  timeline: NetWorthTimelineResult;
  goals?: Goal[];
  birthYear?: number;
  retirementAge?: number;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: NetWorthTimelineResult['points'][number] }>;
  currency: string;
  actualLabel: string;
  projectedLabel: string;
  netWorthLabel: string;
}

function ChartTooltip({
  active,
  payload,
  currency,
  actualLabel,
  projectedLabel,
  netWorthLabel,
}: ChartTooltipProps) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  const phaseLabel =
    point.phase === 'past' || point.phase === 'today' ? actualLabel : projectedLabel;

  return (
    <div className='border-border bg-popover text-popover-foreground rounded-lg border px-3 py-2 shadow-md'>
      <p className='text-muted-foreground text-xs'>
        {formatTooltipLabel(new Date(point.timestamp))}
      </p>
      <p className='mt-1 text-sm font-semibold tabular-nums'>
        {formatCurrency(point.value, currency)}
      </p>
      <p className='text-muted-foreground mt-0.5 text-xs'>
        {netWorthLabel} · {phaseLabel}
      </p>
    </div>
  );
}

function formatYAxis(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${Math.round(value / 1_000)}k`;
  return String(Math.round(value));
}

export function ProjectedNetWorthChart({
  timeline,
  goals = [],
  birthYear,
  retirementAge,
}: ProjectedNetWorthChartProps) {
  const { t } = useLanguage();
  const currency = useDefaultCurrency();
  const { points, goalMarkers, todayTimestamp } = timeline;

  const retirementTimestamp =
    birthYear != null && retirementAge != null
      ? new Date(retirementCalendarYear(birthYear, retirementAge), 6, 1).getTime()
      : undefined;

  const hasRetirement =
    retirementTimestamp != null &&
    points.some((p) => p.timestamp >= todayTimestamp && p.timestamp >= retirementTimestamp);

  const tickTimestamps = [
    points[0]?.timestamp,
    todayTimestamp,
    points[Math.floor(points.length * 0.5)]?.timestamp,
    points[points.length - 1]?.timestamp,
  ].filter((value, index, list): value is number => value != null && list.indexOf(value) === index);

  return (
    <div className='h-[360px] w-full'>
      <ResponsiveContainer width='100%' height='100%'>
        <LineChart data={points} margin={{ top: 20, right: 20, left: 4, bottom: 8 }}>
          <CartesianGrid strokeDasharray='3 3' vertical={false} className='stroke-border/60' />
          <XAxis
            dataKey='timestamp'
            type='number'
            domain={['dataMin', 'dataMax']}
            scale='time'
            ticks={tickTimestamps}
            tick={{ fontSize: 11 }}
            tickFormatter={(value: number) => format(new Date(value), 'MMM yyyy')}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            tickFormatter={formatYAxis}
            axisLine={false}
            tickLine={false}
            width={48}
          />
          <Tooltip
            content={
              <ChartTooltip
                currency={currency}
                actualLabel={t('planning.chart.actual')}
                projectedLabel={t('planning.chart.projected')}
                netWorthLabel={t('planning.chart.netWorthLabel')}
              />
            }
          />
          <ReferenceLine
            x={todayTimestamp}
            stroke='var(--fin-gray-500)'
            strokeDasharray='4 4'
            label={{
              value: t('planning.chart.today'),
              position: 'insideTopLeft',
              fill: 'var(--fin-gray-400)',
              fontSize: 11,
            }}
          />
          {hasRetirement && retirementTimestamp != null && (
            <ReferenceLine
              x={retirementTimestamp}
              stroke='var(--fin-warning)'
              strokeDasharray='4 4'
              label={{
                value: t('planning.chart.retirement'),
                position: 'insideTopRight',
                fill: 'var(--fin-gray-400)',
                fontSize: 11,
              }}
            />
          )}
          <Line
            type='stepAfter'
            dataKey='pastValue'
            stroke='var(--fin-white)'
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
            connectNulls={false}
            isAnimationActive={false}
          />
          <Line
            type='stepAfter'
            dataKey='futureValue'
            stroke='var(--fin-white)'
            strokeWidth={2}
            strokeDasharray='6 4'
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
            connectNulls={false}
            isAnimationActive={false}
          />
          {goalMarkers.map((marker) => (
            <ReferenceDot
              key={marker.id}
              x={marker.timestamp}
              y={marker.value}
              r={5}
              fill='var(--fin-accent)'
              stroke='var(--fin-white)'
              strokeWidth={2}
              ifOverflow='extendDomain'
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      {(goalMarkers.length > 0 || goals.length > 0) && (
        <p className='text-muted-foreground mt-2 text-xs'>{t('planning.chart.goalsLegend')}</p>
      )}
    </div>
  );
}
